/**
 * Sky, sun, clouds and the day/night cycle.
 *
 * The single largest presentation win available: `scene.background` used to be
 * one flat colour, so the top third of every frame was a wall of paint. A real
 * sky costs one inverted sphere with a gradient shader and improves literally
 * every frame in the game (VISUAL_AUDIT V-01).
 *
 * The rig drives five things off one hour value: the sky gradient, the sun's
 * position and colour, the ambient/hemisphere fill, the fog, and whether the
 * neighbourhood's lights are on.
 */
import * as THREE from 'three';

export interface SkyRig {
  group: THREE.Group;
  sun: THREE.DirectionalLight;
  hemi: THREE.HemisphereLight;
  ambient: THREE.AmbientLight;
  /** Drive the whole rig from the in-game hour (0..24). */
  setTimeOfDay(hour: number, scene: THREE.Scene, windowMat: THREE.MeshStandardMaterial): void;
  /**
   * Overcast and rain are the same sky with more cloud, a flatter sun and a
   * greyer fog. Blended rather than switched, so a change of weather at
   * midnight is not a visible cut.
   */
  setWeather(weather: 'sunny' | 'cloudy' | 'light_rain'): void;
  /** Clouds drift; call every frame. */
  update(dt: number): void;
  /**
   * Re-capture the sky as an environment map for image-based lighting.
   *
   * Expensive enough that it is called on a schedule rather than every frame -
   * see `Environment.ts`.
   */
  captureEnvironment(renderer: THREE.WebGLRenderer, scene: THREE.Scene): void;
  /** 0 = full day, 1 = full night. Used to switch lamps and window glow. */
  nightFactor: number;
}

/**
 * Keyframes across the day.
 *
 * `zenith`/`horizon` are the sky gradient; `sun` its colour; `fog` the distance
 * haze, which is deliberately tinted toward the horizon so the world dissolves
 * into the sky rather than into a grey band.
 */
interface SkyKey {
  h: number;
  zenith: number;
  horizon: number;
  sun: number;
  sunI: number;
  hemiSky: number;
  hemiGround: number;
  hemiI: number;
  ambI: number;
  fog: number;
  cloud: number;
  cloudOpacity: number;
  night: number;
}

/**
 * How much of the ambient light comes from the captured sky.
 *
 * The hemisphere and ambient lights in this rig existed *because* there was no
 * environment map: they were a hand-tuned stand-in for sky and bounce light.
 * Now that the real thing is there, they have to come down by the same amount
 * they are being replaced, or the scene simply gains a third ambient source and
 * goes flat and hazy - which is exactly what the first attempt at this looked
 * like. The two factors below are the amount of the old fake that survives.
 */
const ENVIRONMENT_INTENSITY = 0.45;
const HEMI_AFTER_IBL = 0.22;
/**
 * The ambient light takes the deepest cut of the three. It is the flattest
 * source in the rig - identical from every direction - and it is the one the
 * environment map most directly replaces, since a captured sky carries the
 * same fill light but with real directionality.
 */
const AMBIENT_AFTER_IBL = 0.12;
/**
 * The key light goes *up*, not down.
 *
 * An environment map replaces fill light, not sun light. Cutting the fill
 * without strengthening the key leaves a scene that is correctly bright on
 * average and flatter than it was - measured, at first attempt, as the same
 * mean luminance with the standard deviation down from 55.6 to 51.5. Real
 * outdoor contrast comes from a strong key against a weak fill, which is
 * exactly the ratio this restores.
 */
const SUN_AFTER_IBL = 1.22;

const KEYS: SkyKey[] = [
  // Deep night — moonlit blue, not black. A black screen is not atmospheric.
  { h: 0,  zenith: 0x0a1230, horizon: 0x1c2b4d, sun: 0x5b74b8, sunI: 0.16,
    hemiSky: 0x2c3f6b, hemiGround: 0x121722, hemiI: 0.42, ambI: 0.30,
    fog: 0x1c2b4d, cloud: 0x2a3550, cloudOpacity: 0.30, night: 1 },
  // First light
  { h: 5,  zenith: 0x1e3161, horizon: 0x6b5a7a, sun: 0x8f7fb5, sunI: 0.35,
    hemiSky: 0x4a5680, hemiGround: 0x231f26, hemiI: 0.55, ambI: 0.34,
    fog: 0x6b5a7a, cloud: 0x5f5570, cloudOpacity: 0.45, night: 0.75 },
  // Sunrise
  { h: 6, zenith: 0x3f6ba8, horizon: 0xe8956b, sun: 0xffb383, sunI: 1.15,
    hemiSky: 0x8aa6cc, hemiGround: 0x4a3f33, hemiI: 0.80, ambI: 0.44,
    fog: 0xe0a184, cloud: 0xffc9a8, cloudOpacity: 0.75, night: 0.2 },
  // Early morning — the hour the game opens on.
  { h: 7.2, zenith: 0x3f7cbe, horizon: 0xd9bb9a, sun: 0xffe0bb, sunI: 1.55,
    hemiSky: 0x9ab8d8, hemiGround: 0x55503f, hemiI: 0.88, ambI: 0.46,
    fog: 0xcdd3d8, cloud: 0xfff0e2, cloudOpacity: 0.82, night: 0 },
  // Morning
  { h: 8.5, zenith: 0x3d84cc, horizon: 0xa8cbe6, sun: 0xfff0d6, sunI: 1.75,
    hemiSky: 0x9cc4e8, hemiGround: 0x5d5a48, hemiI: 0.92, ambI: 0.46,
    fog: 0xb3d0e8, cloud: 0xffffff, cloudOpacity: 0.85, night: 0 },
  // Midday
  { h: 12.5, zenith: 0x2f7ad1, horizon: 0xbcd9ef, sun: 0xffffff, sunI: 2.05,
    hemiSky: 0xa8ceee, hemiGround: 0x66634f, hemiI: 1.00, ambI: 0.50,
    fog: 0xc4dcef, cloud: 0xffffff, cloudOpacity: 0.80, night: 0 },
  // Afternoon
  { h: 16, zenith: 0x3a80c9, horizon: 0xc6d9e4, sun: 0xfff4e0, sunI: 1.80,
    hemiSky: 0xa4c6e2, hemiGround: 0x635f4a, hemiI: 0.94, ambI: 0.47,
    fog: 0xc6d9e4, cloud: 0xfff6ec, cloudOpacity: 0.82, night: 0 },
  // Golden hour — the money shot. Warm, long shadows, saturated.
  { h: 18.5, zenith: 0x4276b0, horizon: 0xeeb27f, sun: 0xffca92, sunI: 1.42,
    hemiSky: 0xd3b195, hemiGround: 0x5a4c3c, hemiI: 0.92, ambI: 0.48,
    fog: 0xe4b593, cloud: 0xffd9b0, cloudOpacity: 0.88, night: 0.18 },
  // Sunset
  { h: 20, zenith: 0x2b3f7a, horizon: 0xd97a5a, sun: 0xff9a68, sunI: 0.75,
    hemiSky: 0x9a7278, hemiGround: 0x3a302e, hemiI: 0.66, ambI: 0.40,
    fog: 0xc27d68, cloud: 0xe0977a, cloudOpacity: 0.72, night: 0.55 },
  // Dusk
  { h: 21.5, zenith: 0x141e42, horizon: 0x4a3d63, sun: 0x6a6ab0, sunI: 0.28,
    hemiSky: 0x44406b, hemiGround: 0x1a1a24, hemiI: 0.48, ambI: 0.32,
    fog: 0x453a5c, cloud: 0x4a4260, cloudOpacity: 0.5, night: 0.85 },
  { h: 24, zenith: 0x0a1230, horizon: 0x1c2b4d, sun: 0x5b74b8, sunI: 0.16,
    hemiSky: 0x2c3f6b, hemiGround: 0x121722, hemiI: 0.42, ambI: 0.30,
    fog: 0x1c2b4d, cloud: 0x2a3550, cloudOpacity: 0.30, night: 1 },
];

const SKY_VERTEX = /* glsl */ `
  varying vec3 vWorldPosition;
  void main() {
    vec4 worldPosition = modelMatrix * vec4(position, 1.0);
    vWorldPosition = worldPosition.xyz;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

/**
 * Horizon-to-zenith gradient with a sun disc and a broad glow around it.
 * `pow` on the mix keeps the horizon band tight instead of smearing the warm
 * colour halfway up the dome.
 */
const SKY_FRAGMENT = /* glsl */ `
  uniform vec3 uZenith;
  uniform vec3 uHorizon;
  uniform vec3 uSunColour;
  uniform vec3 uSunDirection;
  uniform float uSunIntensity;
  uniform vec3 uCloudColour;
  uniform float uCloudOpacity;
  uniform float uCloudCoverage;
  uniform float uTime;
  varying vec3 vWorldPosition;

  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
  }

  float valueNoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(
      mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
      mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
      u.y);
  }

  float fbm(vec2 p) {
    float value = 0.0;
    float amplitude = 0.5;
    for (int i = 0; i < 5; i++) {
      value += amplitude * valueNoise(p);
      p *= 2.03;
      amplitude *= 0.5;
    }
    return value;
  }

  /**
   * Density of the cloud deck along a view direction.
   *
   * The direction is projected onto a horizontal plane, so the deck compresses
   * toward the horizon exactly the way a real overcast does. Sampling a flat
   * noise field on a dome instead would smear the clouds evenly across the sky
   * and read as wallpaper.
   */
  float cloudDensity(vec3 direction, vec2 offset) {
    float reach = 1.0 / max(direction.y, 0.04);
    vec2 uv = direction.xz * reach * 0.42 + offset;
    uv += vec2(uTime * 0.0065, uTime * 0.0022);
    float n = fbm(uv * 2.1);
    // Break up the base shape so edges are ragged rather than blobby.
    n = n * 0.80 + fbm(uv * 5.6) * 0.20;
    return smoothstep(uCloudCoverage, uCloudCoverage + 0.20, n);
  }

  void main() {
    vec3 direction = normalize(vWorldPosition);
    float height = clamp(direction.y, 0.0, 1.0);
    vec3 colour = mix(uHorizon, uZenith, pow(height, 0.55));

    vec3 sunDir = normalize(uSunDirection);
    float sunDot = max(dot(direction, sunDir), 0.0);
    // Broad atmospheric glow, then the disc itself.
    colour += uSunColour * pow(sunDot, 12.0) * 0.35 * uSunIntensity;
    colour += uSunColour * smoothstep(0.9975, 0.9995, sunDot) * 1.4;

    // --- cloud deck --------------------------------------------------------
    float density = cloudDensity(direction, vec2(0.0));
    // A second sample shifted toward the sun gives the mass a lit edge and a
    // shaded body for free, without a second geometry pass.
    float lit = cloudDensity(direction, sunDir.xz * -0.09);
    // Clouds must not stack up on the horizon line, where the deck is edge-on.
    float horizonFade = smoothstep(0.015, 0.20, direction.y);
    float coverAlpha = density * horizonFade * uCloudOpacity;

    vec3 cloudLit = uCloudColour + uSunColour * pow(sunDot, 6.0) * 0.30;
    // A well-defined shaded underside is what separates a cloud from a smudge.
    vec3 cloudShaded = uCloudColour * 0.58;
    vec3 cloud = mix(cloudShaded, cloudLit, clamp(lit, 0.0, 1.0));

    colour = mix(colour, cloud, clamp(coverAlpha, 0.0, 1.0));

    gl_FragColor = vec4(colour, 1.0);
  }
`;

export function createSky(): SkyRig {
  const group = new THREE.Group();
  let cloudTime = 0;
  // 0 = clear, 1 = fully overcast. Eased toward the target every frame.
  let overcast = 0;
  let overcastTarget = 0;

  // --- gradient dome -------------------------------------------------------
  const skyUniforms = {
    uZenith: { value: new THREE.Color(0x3d84cc) },
    uHorizon: { value: new THREE.Color(0xa8cbe6) },
    uSunColour: { value: new THREE.Color(0xfff0d6) },
    uSunDirection: { value: new THREE.Vector3(0, 1, 0) },
    uSunIntensity: { value: 1 },
    uCloudColour: { value: new THREE.Color(0xffffff) },
    uCloudOpacity: { value: 0.85 },
    uCloudCoverage: { value: 0.47 },
    uTime: { value: 0 },
  };
  const dome = new THREE.Mesh(
    new THREE.SphereGeometry(480, 32, 20),
    new THREE.ShaderMaterial({
      uniforms: skyUniforms,
      vertexShader: SKY_VERTEX,
      fragmentShader: SKY_FRAGMENT,
      side: THREE.BackSide,
      depthWrite: false,
      fog: false,
    }),
  );
  dome.renderOrder = -1000;
  group.add(dome);

  // Clouds live in the dome shader (see SKY_FRAGMENT). An earlier version used
  // eighty billboard discs; they were invisible from ground level, cost eighty
  // draw calls, and could not be lit by the sun.


  // --- lights --------------------------------------------------------------
  const sun = new THREE.DirectionalLight(0xffffff, 1.8);
  sun.castShadow = true;
  // 1024 over a 62 m box is about 16 texels per metre, which is plenty for
  // this art style, and it is four times cheaper to rasterise than 2048 -
  // the dominant per-frame cost in the performance pass.
  sun.shadow.mapSize.set(1024, 1024);
  sun.shadow.camera.near = 1;
  sun.shadow.camera.far = 340;
  // Tighter than the whole neighbourhood: the shadow camera follows the player,
  // so a smaller extent buys much sharper contact shadows where they are seen.
  const extent = 62;
  sun.shadow.camera.left = -extent;
  sun.shadow.camera.right = extent;
  sun.shadow.camera.top = extent;
  sun.shadow.camera.bottom = -extent;
  sun.shadow.bias = -0.0006;
  sun.shadow.normalBias = 0.035;
  group.add(sun);
  group.add(sun.target);

  const hemi = new THREE.HemisphereLight(0x9cc4e8, 0x5d5a48, 0.9);
  group.add(hemi);

  const ambient = new THREE.AmbientLight(0xffffff, 0.45);
  group.add(ambient);

  // Built lazily: a PMREM generator compiles shaders, and there is no reason to
  // pay for that before the first capture.
  let pmrem: THREE.PMREMGenerator | null = null;
  let envTarget: THREE.WebGLRenderTarget | null = null;
  /** A scene holding only the dome, so the environment is sky and nothing else. */
  let envScene: THREE.Scene | null = null;

  const rig: SkyRig = {
    group, sun, hemi, ambient, nightFactor: 0,
    setTimeOfDay, update,
    captureEnvironment(renderer: THREE.WebGLRenderer, target: THREE.Scene): void {
      if (!pmrem) {
        pmrem = new THREE.PMREMGenerator(renderer);
        pmrem.compileEquirectangularShader();
      }
      if (!envScene) {
        envScene = new THREE.Scene();
        // The dome is shared: parent it to the capture scene for the duration
        // of the render, then hand it straight back. Cloning it would mean two
        // copies of the shader drifting apart at different times of day.
        envScene.add(dome);
      } else {
        envScene.add(dome);
      }

      const previous = envTarget;
      envTarget = pmrem.fromScene(envScene, 0, 0.1, 1000);
      group.add(dome);
      target.environment = envTarget.texture;
      target.environmentIntensity = ENVIRONMENT_INTENSITY;
      previous?.dispose();
    },
    setWeather(weather): void {
      overcastTarget = weather === 'light_rain' ? 1 : weather === 'cloudy' ? 0.82 : 0;
    },
  };

  function setTimeOfDay(
    hour: number,
    scene: THREE.Scene,
    windowMat: THREE.MeshStandardMaterial,
  ): void {
    const h = ((hour % 24) + 24) % 24;
    let a = KEYS[0]!;
    let b = KEYS[KEYS.length - 1]!;
    for (let i = 0; i < KEYS.length - 1; i++) {
      if (h >= KEYS[i]!.h && h <= KEYS[i + 1]!.h) {
        a = KEYS[i]!;
        b = KEYS[i + 1]!;
        break;
      }
    }
    // Smoothstep the blend so transitions ease rather than ramp linearly.
    const raw = Math.max(0, Math.min(1, (h - a.h) / ((b.h - a.h) || 1)));
    const t = raw * raw * (3 - 2 * raw);

    const zenith = lerpColour(a.zenith, b.zenith, t);
    const horizon = lerpColour(a.horizon, b.horizon, t);
    const sunColour = lerpColour(a.sun, b.sun, t);
    const fogColour = lerpColour(a.fog, b.fog, t);

    skyUniforms.uZenith.value.copy(zenith);
    skyUniforms.uHorizon.value.copy(horizon);
    skyUniforms.uSunColour.value.copy(sunColour);
    skyUniforms.uSunIntensity.value = lerp(a.sunI, b.sunI, t);

    // The dome is the background, so the scene background only matters where
    // the dome is not drawn. Keep them consistent anyway.
    scene.background = null;
    // Both Fog and FogExp2 carry a colour; the guard used to name the linear
    // class, so switching to exponential fog would have silently frozen the
    // haze at its dawn colour for the rest of the day.
    if (scene.fog) scene.fog.color.copy(fogColour);

    hemi.color.copy(lerpColour(a.hemiSky, b.hemiSky, t));
    hemi.groundColor.copy(lerpColour(a.hemiGround, b.hemiGround, t));
    hemi.intensity = lerp(a.hemiI, b.hemiI, t) * HEMI_AFTER_IBL;
    ambient.intensity = lerp(a.ambI, b.ambI, t) * AMBIENT_AFTER_IBL;
    sun.color.copy(sunColour);
    sun.intensity = lerp(a.sunI, b.sunI, t) * SUN_AFTER_IBL;

    // Sun arc: rises around 06:00, peaks near 13:00, sets around 20:00. Below
    // the horizon it is kept just above zero so shadows do not flip wildly.
    // Sunrise 05:30, sunset 20:30. The game starts at 07:00 and that first
    // frame is the player's first impression, so it must read as morning, not
    // as the sun still stuck on the horizon.
    const dayProgress = (h - 5.5) / 15;
    const angle = Math.PI * dayProgress;
    const elevation = Math.sin(angle);
    const sunDirection = new THREE.Vector3(
      Math.cos(angle) * 0.9,
      Math.max(0.06, elevation),
      0.34 + Math.cos(angle) * 0.28,
    ).normalize();
    skyUniforms.uSunDirection.value.copy(sunDirection);
    sun.position.copy(sunDirection).multiplyScalar(150).add(sun.target.position);

    const night = lerp(a.night, b.night, t);
    rig.nightFactor = night;

    // --- weather -----------------------------------------------------------
    // Overcast is one number applied at the end, so every keyframe stays a
    // description of the hour and none of them has to know about the weather.
    const cloud = lerpColour(a.cloud, b.cloud, t);
    if (overcast > 0.001) {
      const grey = new THREE.Color(0x9aa2ad).lerp(new THREE.Color(0x3f454e), night);
      cloud.lerp(grey, overcast * 0.7);
      skyUniforms.uZenith.value.lerp(grey, overcast * 0.55);
      skyUniforms.uHorizon.value.lerp(grey, overcast * 0.65);
      if (scene.fog) scene.fog.color.lerp(grey, overcast * 0.7);
      // Rain thickens the air as well as greying it.
      if (scene.fog instanceof THREE.FogExp2) {
        scene.fog.density = 0.0028 * (1 + overcast * 0.55);
      }
      // The sun goes flat and the sky fills in behind it.
      sun.intensity *= 1 - overcast * 0.62;
      skyUniforms.uSunIntensity.value *= 1 - overcast * 0.8;
      hemi.intensity *= 1 - overcast * 0.12;
      ambient.intensity *= 1 + overcast * 0.18;
    }
    skyUniforms.uCloudColour.value.copy(cloud);
    skyUniforms.uCloudOpacity.value =
      lerp(a.cloudOpacity, b.cloudOpacity, t) * (1 - overcast) + overcast;
    skyUniforms.uCloudCoverage.value = 0.47 - overcast * 0.42;

    // Interior and shop lights come on as it gets dark. Eased rather than
    // snapped, because a whole street switching on in one frame reads as a bug.
    const target = night * 0.95;
    windowMat.emissiveIntensity += (target - windowMat.emissiveIntensity) * 0.15;
  }

  function update(dt: number): void {
    overcast += (overcastTarget - overcast) * Math.min(1, dt * 0.6);
    // Wraps every ~2.9 hours of real time, long before float precision bites.
    cloudTime = (cloudTime + dt) % 10000;
    skyUniforms.uTime.value = cloudTime;
  }

  return rig;
}

/** Keep the shadow camera centred on the player so its budget is not wasted. */
export function followShadowCamera(rig: SkyRig, x: number, z: number): void {
  const direction = rig.sun.position.clone().sub(rig.sun.target.position);
  rig.sun.target.position.set(x, 0, z);
  rig.sun.target.updateMatrixWorld();
  rig.sun.position.copy(rig.sun.target.position).add(direction);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function lerpColour(a: number, b: number, t: number): THREE.Color {
  return new THREE.Color(a).lerp(new THREE.Color(b), t);
}
